/* eslint-disable react-hooks/exhaustive-deps */
import { SessionsToReschedule } from '@kouto/types';
import { z } from 'zod';

import { parseISO, format, addMinutes } from 'date-fns';
import { DaySchedule, TimeSlot } from 'features/GuestReschedule/types';
import useRescheduleSessions from 'api/reschedule/useRescheduleSessions';

const groupSessionsByDay = (
  sessions: z.infer<typeof SessionsToReschedule>,
): DaySchedule[] => {
  if (sessions.length === 0) {
    return [];
  }

  const grouped: Record<string, TimeSlot[]> = {};

  sessions.forEach((session) => {
    const start = parseISO(session.startDateTime);
    const end = addMinutes(start, session.sessionDuration);

    const dateKey = format(start, 'yyyy-MM-dd');

    const timeSlot: TimeSlot = {
      startTime: format(start, 'h:mm a'),
      endTime: format(end, 'h:mm a'),
      isAvailable: session.status === 'OPEN',
      value: session.startDateTime,
      experienceId: session.experienceId,
    };

    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }

    grouped[dateKey].push(timeSlot);
  });

  return Object.entries(grouped).map(([date, slots]) => ({
    date,
    slots,
  }));
};

export const useGuestRescheduleSessions = (
  bookingId: string | undefined,
  from?: string,
  to?: string,
) => {
  const skip = !bookingId;

  const { data, isLoading } = useRescheduleSessions(bookingId ?? '', {
    from,
    to,
    skip,
  });

  const resourceGroupExperiences =
    data?.resourceGroupCollections
      .filter((collection) => collection.isMapShown)
      .flatMap((collection) => collection.resourceGroups)
      .filter((group) =>
        group.experiences.some(({ id }) => id === data?.experienceId),
      )
      .flatMap((group) => group.experiences)
      .sort((a, b) =>
        a.title.localeCompare(b.title, undefined, { numeric: true }),
      ) ?? [];

  return {
    isLoading,
    sessions: groupSessionsByDay(data?.sessions ?? []),
    experienceId: data?.experienceId ?? '',
    resources: resourceGroupExperiences.map(({ id, title }) => ({
      value: id,
      label: title,
    })),
  };
};
